import React from 'react';
import { createContext, useContext } from 'react';
import { PostCodeGroupType, PostCodeType } from '../types/post_code';
import { useReducerAsState } from '../hooks/useReducerAsState';
import PostCodeController from '../controllers/postCode.controller';

interface State {
    items: Array<PostCodeType>;
    item: PostCodeType | null;
    total: number;
}

type PostCodeContextType = State & {
    getAllPostalCodes: (
        keywords?: string,
        page?: number,
        appendResults?: boolean
    ) => Promise<string>;
    createPostCode: (data: PostCodeType) => Promise<string>;
    updatePostCode: (data: PostCodeType) => Promise<string>;
    getPostCode: (postCode: number) => Promise<PostCodeType | string>;
    deletePostCode: (postCode: number) => Promise<string>;
    refreshWeatherData: (postCode: number) => Promise<string>;
};

interface Props {
    children: React.ReactElement;
}

const PostCodeContext = createContext<PostCodeContextType>({
    items: [],
    item: null,
    total: 0,
    getAllPostalCodes: () => Promise.resolve(''),
    createPostCode: () => Promise.resolve(''),
    updatePostCode: () => Promise.resolve(''),
    getPostCode: () => Promise.resolve(''),
    deletePostCode: () => Promise.resolve(''),
    refreshWeatherData: () => Promise.resolve('')
});

export const PostCodeProvider = ({ children }: Props) => {
    const [state, setState] = useReducerAsState<State>({
        items: [],
        item: null,
        total: 0
    });

    const { items, item, total } = state;

    const getAllPostalCodes = async (
        keywords?: string,
        page?: number,
        appendResults?: boolean
    ): Promise<string> => {
        const response = await PostCodeController.getAll(keywords ?? '', page ?? 1);
        setState({ items: [], total: 0 });
        if (!response.success) {
            setState({ items: [], total: 0 });
            return response.errorMessage ?? 'Error al obtener los códigos postales';
        }

        const data = response.result as unknown as PostCodeGroupType;

        setState({
            items: appendResults ? state.items.concat(data.postCodes) : data.postCodes,
            total: data.total
        });
        return '';
    };

    const getPostCode = async (id: number): Promise<PostCodeType | string> => {
        const response = await PostCodeController.find(id);
        setState({ item: null });
        if (!response.success) {
            return response.errorMessage ?? 'Error al obtener código postal';
        }

        // eslint-disable-next-line
        const data: any | null = response.result.data ? response.result.data : null;
        const postCode = data['postalCode'] ? data['postalCode'] : null;

        if (!postCode) {
            return 'No se encontró ningún código postal';
        }
        setState({ item: postCode });
        return postCode;
    };

    const createPostCode = async (postCode: PostCodeType): Promise<string> => {
        const response = await PostCodeController.create(postCode);
        if (!response.success) {
            return response.errorMessage ?? 'Error al crear el código postal';
        }

        // eslint-disable-next-line
        const data: any | null = response.result.data ? response.result.data : null;
        const newPostCode = data['postalCode'] ? data['postalCode'] : null;
        if (data === null || newPostCode === null) {
            return 'Error al crear código postal';
        }

        setState({
            items: [newPostCode, ...state.items],
            total: state.total + 1
        });
        return '';
    };

    const updatePostCode = async (postCode: PostCodeType): Promise<string> => {
        const response = await PostCodeController.update(postCode);
        if (!response.success) {
            return response.errorMessage ?? 'Error al actualizar código postal';
        }

        // eslint-disable-next-line
        const data: any | null = response.result.data ? response.result.data : null;
        const newPostCode = data['postalCode'] ? data['postalCode'] : null;
        if (data === null || newPostCode === null) {
            return 'Error al actualizar código postal';
        }

        setState({
            items: state.items.map((item) => (item.id === postCode.id ? newPostCode : item)),
            item: newPostCode,
            total: state.total
        });
        return '';
    };

    const deletePostCode = async (postCodeId: number): Promise<string> => {
        const response = await PostCodeController.delete(postCodeId);
        if (!response.success || response.statusCode === 500) {
            return response.errorMessage ?? 'Error al eliminar código postal';
        }

        if (response.statusCode === 404) {
            return 'No se encontró ningún código postal';
        }

        setState({
            items: state.items.filter((item) => item.id !== postCodeId),
            item: null,
            total: state.total - 1
        });

        return '';
    };

    const refreshWeatherData = async (postCodeId: number): Promise<string> => {
        const response = await PostCodeController.refreshWeatherData(postCodeId);
        if (!response.success || response.statusCode === 500) {
            return response.errorMessage ?? 'Error al obtener información detallada';
        }

        if (response.statusCode === 404) {
            return 'No se encontró ningún código postal';
        }

        // DC: update info field with new data
        const infoData = response.result.data as unknown as string;

        setState({
            item: undefined
        });
        return infoData;
    };

    const value: PostCodeContextType = {
        getAllPostalCodes,
        createPostCode,
        updatePostCode,
        getPostCode,
        deletePostCode,
        refreshWeatherData,
        items,
        item,
        total
    };

    return <PostCodeContext.Provider value={value}>{children}</PostCodeContext.Provider>;
};

export const usePostCodeContext = () => {
    return useContext(PostCodeContext);
};
